import { useState, useEffect, useMemo, useCallback } from "react";
import { useOutletContext, useSearchParams } from "react-router-dom";
import { Calculator, Inbox, Wallet, TrendingUp, Landmark, LineChart, Lock, Bell } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import api from "../lib/api";
import FinanceOverview from "../components/finance/FinanceOverview";
import DocumentsInbox from "../components/finance/DocumentsInbox";
import ExpensesList from "../components/finance/ExpensesList";
import BankReconciliation from "../components/finance/BankReconciliation";
import CashFlowForecast from "../components/finance/CashFlowForecast";
import ClosingsTab from "../components/finance/ClosingsTab";
import FiscalNotificationsCenter from "../components/finance/FiscalNotificationsCenter";
import { PageHeader } from "../components/layout/PageHeader";

/** Helpers */
const fmtEUR = (n) =>
  new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n || 0);

const firstDayOfMonth = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
};
const today = () => new Date().toISOString().slice(0, 10);

export default function FinancePage() {
  const { currentRestaurant } = useOutletContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState(searchParams.get("tab") || "overview");
  const [categories, setCategories] = useState([]);
  const [range, setRange] = useState({ from: firstDayOfMonth(), to: today() });
  const [summary, setSummary] = useState(null);
  const [trend, setTrend] = useState(null);
  const [bankStats, setBankStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const handleTabChange = (v) => {
    setTab(v);
    const next = new URLSearchParams(searchParams);
    if (v === "overview") next.delete("tab"); else next.set("tab", v);
    setSearchParams(next);
  };

  useEffect(() => {
    api.getFinanceCategories().then(setCategories).catch(() => setCategories([]));
  }, []);

  const loadSummary = useCallback(async () => {
    if (!currentRestaurant?.restaurant_id) return;
    setLoading(true);
    try {
      const [s, t, bs] = await Promise.all([
        api.getFinanceSummary(currentRestaurant.restaurant_id, { ...range, compare: true }),
        api.getFinanceTrend(currentRestaurant.restaurant_id, 12).catch(() => null),
        api.getBankTransactionStats(currentRestaurant.restaurant_id).catch(() => null),
      ]);
      setSummary(s);
      setTrend(t);
      setBankStats(bs);
    } catch {
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, [currentRestaurant, range]);

  useEffect(() => { loadSummary(); }, [loadSummary]);

  const catLabels = useMemo(
    () => Object.fromEntries((categories || []).map(c => [c.value, c.label])),
    [categories]
  );

  return (
    <div className="space-y-6 max-w-6xl" data-testid="finance-page">
      <PageHeader
        icon={<Calculator className="w-5 h-5" />}
        title="Contabilidad"
        description="Ingresos, gastos y documentos del restaurante. P&L en vivo."
        breadcrumb={[{ label: "Finanzas y análisis" }, { label: "Contabilidad" }]}
      />

      <Tabs value={tab} onValueChange={handleTabChange}>
        <TabsList data-testid="finance-tabs">
          <TabsTrigger value="overview" data-testid="tab-overview">
            <TrendingUp className="h-3.5 w-3.5 mr-1.5" /> Resumen
          </TabsTrigger>
          <TabsTrigger value="inbox" data-testid="tab-inbox">
            <Inbox className="h-3.5 w-3.5 mr-1.5" /> Bandeja
            {summary?.pending_docs > 0 && (
              <span className="ml-1.5 inline-flex items-center justify-center text-[10px] font-bold min-w-[18px] h-[18px] rounded-full bg-red-500 text-white px-1">
                {summary.pending_docs}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="expenses" data-testid="tab-expenses">
            <Wallet className="h-3.5 w-3.5 mr-1.5" /> Gastos
          </TabsTrigger>
          <TabsTrigger value="bank" data-testid="tab-bank">
            <Landmark className="h-3.5 w-3.5 mr-1.5" /> Banco
            {bankStats?.unmatched > 0 && (
              <span className="ml-1.5 inline-flex items-center justify-center text-[10px] font-bold min-w-[18px] h-[18px] rounded-full bg-amber-500 text-white px-1">
                {bankStats.unmatched}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="forecast" data-testid="tab-forecast">
            <LineChart className="h-3.5 w-3.5 mr-1.5" /> Previsión
          </TabsTrigger>
          <TabsTrigger value="closings" data-testid="tab-closings">
            <Lock className="h-3.5 w-3.5 mr-1.5" /> Cierres
          </TabsTrigger>
          <TabsTrigger value="notifications" data-testid="tab-notifications">
            <Bell className="h-3.5 w-3.5 mr-1.5" /> Notificaciones
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-5">
          <FinanceOverview
            restaurantId={currentRestaurant?.restaurant_id}
            range={range}
            onRangeChange={setRange}
            summary={summary}
            trend={trend}
            loading={loading}
            catLabels={catLabels}
            fmtEUR={fmtEUR}
            onNavigateTab={handleTabChange}
          />
        </TabsContent>

        <TabsContent value="inbox" className="mt-5">
          <DocumentsInbox
            restaurantId={currentRestaurant?.restaurant_id}
            categories={categories}
            catLabels={catLabels}
            onChange={loadSummary}
          />
        </TabsContent>

        <TabsContent value="expenses" className="mt-5">
          <ExpensesList
            restaurantId={currentRestaurant?.restaurant_id}
            categories={categories}
            catLabels={catLabels}
            range={range}
            onChange={loadSummary}
          />
        </TabsContent>

        <TabsContent value="bank" className="mt-5">
          <BankReconciliation
            restaurantId={currentRestaurant?.restaurant_id}
            categories={categories}
            catLabels={catLabels}
            onChange={loadSummary}
          />
        </TabsContent>

        <TabsContent value="forecast" className="mt-5">
          <CashFlowForecast restaurantId={currentRestaurant?.restaurant_id} />
        </TabsContent>

        <TabsContent value="closings" className="mt-5">
          <ClosingsTab restaurantId={currentRestaurant?.restaurant_id} />
        </TabsContent>

        <TabsContent value="notifications" className="mt-5">
          <FiscalNotificationsCenter restaurantId={currentRestaurant?.restaurant_id} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
